import { Prefs } from '../../../application/PreferenceNames';
import * as dte from '../../DtEventTypes';
import { MIN_MS } from './time';
import { lerp } from '../../../tools/viewtransitions/ViewTransition';

const DEFAULT_TOTAL_STEPS = 500; // this along with the frames per seconds gives the total duration at 1x.

export class Player {
  loop = false;
  speed = 1;
  fps = 10;

  numSteps = DEFAULT_TOTAL_STEPS;

  #intervalID;

  get isPlaying() {
    return !!this.#intervalID;
  }

  play(start = 0) {
    if (this.isPlaying) {
      this.stop();
    }

    let step = start * this.numSteps;
    const tick = () => {
      if (step > this.numSteps) {
        if (this.loop) {
          step = 0;
        } else {
          this.onValueChanged(1.0);
          this.stop();
          return;
        }
      }

      this.onValueChanged(step / this.numSteps);
      step += this.speed;
    };

    this.#intervalID = setInterval(tick, 1_000 / this.fps);
    this.onConfigChanged();
  }

  stop() {
    if (this.isPlaying) {
      clearInterval(this.#intervalID);
      this.#intervalID = null;
      this.onConfigChanged();
    }
  }

  setLoop(loop) {
    if (this.loop !== loop) {
      this.loop = loop;
      this.onConfigChanged();
    }
  }

  setSpeed(speed) {
    if (this.speed !== speed) {
      this.speed = speed;
      this.onConfigChanged();
    }
  }

  dispose() {
    this.stop();
    this.loop = false;
    this.speed = 1;
  }

  // When the config changes (ex. speed, play, stop, etc.)
  onConfigChanged() {/* to override */}

  // Callback for when the player changes value [0.0, 1.0]
  onValueChanged() {/* to override */}
}

export class TimelinePlayer extends Player {
  hadProgressiveDisplay;
  view;
  viewer;

  constructor(telemetryView, viewer, eventSource) {
    super();
    this.view = telemetryView;
    this.viewer = viewer;
    this.eventSource = eventSource;

    // When displaying the heatmap, we turn progressive off while playing to stop the facility from flickering
    // at the cost of possibly delaying ticks.
    this.shouldTurnProgressiveOff = false;

    this.boundOnHeatmapChanged = this.onHeatmapChanged.bind(this);
    this.eventSource.addEventListener(dte.DT_HEATMAP_CHANGED_EVENT, this.boundOnHeatmapChanged);
  }

  onHeatmapChanged({ change: { attrIdFilter, ctype } }) {
    if (ctype !== 'shading') return;

    this.shouldTurnProgressiveOff = Boolean(attrIdFilter);
  }

  dispose() {
    this.eventSource.removeEventListener(dte.DT_HEATMAP_CHANGED_EVENT, this.boundOnHeatmapChanged);

    super.dispose();
  }

  onConfigChanged() {
    this.view.dispatchEvent({ type: dte.DT_TELEMETRY_CHANGED_EVENT, player: this });
  }

  onValueChanged(z) {
    const { beg, end } = this.view.resolve('UTC');

    const ts = lerp(beg, end, z);
    const nearestMin = Math.round(ts / MIN_MS) * MIN_MS;
    this.view.setTime(nearestMin, true);
  }

  play() {
    // Progressive display truncates the rendering of model elements if they take too long to render which
    // induces a flickering effect. Given heatmap refreshes often, we disable it temporarily.
    this.hadProgressiveDisplay = this.shouldTurnProgressiveOff && this.viewer.prefs.get(Prefs.PROGRESSIVE_RENDERING);
    if (this.hadProgressiveDisplay) {
      console.info('TimelinePlayer is turning off progressive display for the animation.');
      this.viewer.impl.toggleProgressive(false);
    }

    let start = 0;
    if (this.view.time.focus !== this.view.interval.end) {
      const { beg, end, time } = this.view.resolve('UTC');
      start = invLerp(time, beg, end);
    }


    super.play(start);
  }

  stop() {
    super.stop();

    if (this.hadProgressiveDisplay) {
      this.viewer.impl.toggleProgressive(true);
      delete this.hadProgressiveDisplay;
    }
  }
}

const invLerp = (v, min, max) => (v - min) / (max - min);